/// <reference types="vite/client" />
import { ofetch } from 'ofetch'
import { useCookie } from '../@core/composable/useCookie'
import { getI18n } from '@/plugins/i18n'
import { resolveActiveAppId } from './activeApp'

const getMaintenancePath = () => {
  if (typeof window === 'undefined')
    return '/under-maintenance'

  return window.location.pathname.startsWith('/build/')
    ? '/build/under-maintenance'
    : '/under-maintenance'
}

const redirectToMaintenancePage = () => {
  if (typeof window === 'undefined')
    return

  const maintenancePath = getMaintenancePath()
  if (window.location.pathname === maintenancePath)
    return

  window.location.assign(maintenancePath)
}

const getLoginPath = () => {
  if (typeof window === 'undefined')
    return '/build/login'

  return window.location.pathname.startsWith('/build/')
    ? '/build/login'
    : '/login'
}

// I browser rifiutano in silenzio la scrittura di un cookie oltre ~4KB,
// lasciando il valore precedente invariato: userData.allowed_apps (un array
// di app complete, illimitato per tenant - 34 app = 3,3KB da solo per il
// tenant 4) fa superare quel limite per qualunque tenant con molte app,
// quindi il cookie 'userData' smette silenziosamente di aggiornarsi (bug
// reale 2026-10-12, scoperto durante un'impersonazione: il menu sidebar
// restava quello della sessione admin precedente mentre il contenuto pagina,
// autenticato via header e non via cookie, mostrava correttamente i dati del
// tenant impersonato). Nessun codice rilegge allowed_apps dal COOKIE: l'unico
// consumatore (login.vue) lo legge dalla risposta API in memoria, prima
// ancora che venga scritta qui - va quindi escluso da tutto cio' che scrive
// su useCookie('userData'), non solo da questo punto.
export const setUserDataCookie = (data: any) => {
  if (!data) {
    useCookie('userData').value = null
    return
  }

  const { allowed_apps, ...rest } = data
  useCookie('userData').value = rest
}

const clearAuthState = () => {
  useCookie('accessToken').value = null
  useCookie('userData').value = null
  useCookie('userAbilityRules').value = null

  if (typeof window === 'undefined')
    return

  try {
    localStorage.removeItem('activeAppId')
    localStorage.removeItem('activeAppName')
    localStorage.removeItem('tenantId')
  }
  catch {}
}

const redirectToLoginPage = () => {
  if (typeof window === 'undefined')
    return

  const loginPath = getLoginPath()
  if (window.location.pathname === loginPath)
    return

  window.location.assign(loginPath)
}

const shouldSkipAppContext = () => {
  if (typeof window === 'undefined')
    return false

  return /\/settings\/(whitelabel|reseller-provisioning|backend-user-notifications|backend-smtp|backend-whatsender)(\/|$)/.test(window.location.pathname)
}

export const $api = ofetch.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  credentials: 'include',
  async onRequest({ options }) {
    const accessToken = useCookie('accessToken').value
    
    // Ensure headers object exists and handle both Headers and plain object types
    options.headers = options.headers || {}
    
    if (accessToken) {
      if (options.headers instanceof Headers) {
        options.headers.append('Authorization', `Bearer ${accessToken}`)
      } else {
        // @ts-ignore
        options.headers.Authorization = `Bearer ${accessToken}`
      }
    }

    // Add CSRF token for SPA authentication
    const csrfToken = useCookie('XSRF-TOKEN').value
    if (csrfToken) {
      if (options.headers instanceof Headers) {
        options.headers.append('X-XSRF-TOKEN', csrfToken)
      } else {
        // @ts-ignore
        options.headers['X-XSRF-TOKEN'] = csrfToken
      }
    }

    // Tenancy context: propagate tenant id/domain when available
    try {
      const tenantId = localStorage.getItem('tenantId')
      if (tenantId) {
        if (options.headers instanceof Headers) {
          options.headers.append('X-Tenant-ID', tenantId)
        } else {
          // @ts-ignore
          options.headers['X-Tenant-ID'] = tenantId
        }
      }

      // App context - fallback su userData.default_app_id quando la cache locale manca (stesso
      // bug/causa gia' corretto in client.ts/guards.ts il 2026-10-20: browser/dispositivo diverso
      // da quello del login, storage svuotato, ecc.)
      const activeAppId = localStorage.getItem('activeAppId') || resolveActiveAppId(useCookie('userData').value)
      if (activeAppId && !shouldSkipAppContext()) {
        if (options.headers instanceof Headers) {
          options.headers.append('X-App-ID', activeAppId)
        } else {
          // @ts-ignore
          options.headers['X-App-ID'] = activeAppId
        }
      }
    }
    catch {}

    try {
      const locale = getI18n().global.locale.value
      if (locale) {
        if (options.headers instanceof Headers) {
          options.headers.append('X-Locale', locale)
          options.headers.append('Accept-Language', locale)
        } else {
          // @ts-ignore
          options.headers['X-Locale'] = locale
          // @ts-ignore
          options.headers['Accept-Language'] = locale
        }
      }
    }
    catch {}

    try {
      const domain = location?.hostname
      if (domain) {
        if (options.headers instanceof Headers) {
          options.headers.append('X-Tenant-Domain', domain)
        } else {
          // @ts-ignore
          options.headers['X-Tenant-Domain'] = domain
        }
      }
    }
    catch {}
  },
  async onResponseError({ request, response, options }) {
    const payload = response?._data as Record<string, any> | undefined
    if (response?.status === 503 && payload?.maintenance_mode)
      redirectToMaintenancePage()

    const requestUrl = String(request || '')
    const hasAuthHeader = options?.headers instanceof Headers
      ? options.headers.has('Authorization')
      : Boolean((options?.headers as Record<string, any> | undefined)?.Authorization)
    const isAuthEndpoint = requestUrl.includes('/auth/login')
      || requestUrl.includes('/auth/social-login')
      || requestUrl.includes('/auth/register')

    if ((response?.status === 401 || response?.status === 423) && hasAuthHeader && !isAuthEndpoint) {
      clearAuthState()
      redirectToLoginPage()
    }
  },
})
